import { useEffect, useState } from 'react';

import Img from 'components/Img';
import LinkCard from 'components/LinkCard';
import Popover from 'components/Popover';
import { PriceText } from 'components/Price';
import ScreenReaderAnnouncementText from 'components/ScreenReaderAnnouncementText';
import Text from 'components/Text';
import { publicEnvConfig } from 'config/public';
import { usePriceContext } from 'contexts';
import type { FieldKey } from 'hooks';
import { useCampaignProducts } from 'hooks/data-fetching';
import { useDateFormatter } from 'hooks/format';
import { useProductListGTMEvents } from 'hooks/gtm';
import type { FormattedPrice } from 'models/api';
import type { AssetResponse } from 'models/asset';
import type { ComparisonPrice } from 'models/price';
import type { DiscountType } from 'models/product';
import type { CampaignId, DateString, TemplateString } from 'types';
import { isDateAfter, subtractDaysFromDate } from 'utils/date';
import { formatTemplate } from 'utils/format';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import CampaignBuyAllProducts, {
	type AddCampaignData,
} from './CampaignBuyAllProducts';
import CampaignBuyAnyProducts from './CampaignBuyAnyProducts';

interface Props {
	campaignValidToText: FieldKey | TemplateString;
	comparisonPrice?: ComparisonPrice | undefined;
	discountType: DiscountType;
	id: CampaignId;
	images: AssetResponse[] | undefined;
	onCampaignPackageAddToCartClick:
		| ((campaign: AddCampaignData) => Promise<void>)
		| undefined;
	onCampaignPackageAddToWishlistClick:
		| ((campaign: AddCampaignData) => Promise<void>)
		| undefined;
	priceLabel?: string;
	// Only staffling/BuyAllToLoweredUnitPrice campaigns have a price for the
	// intial campaign data and only staffling should display it on the LinkCard.
	stafflingPrice: FormattedPrice | undefined;
	title: string;
	validToDate: DateString | undefined;
}

export default function Campaign({
	campaignValidToText,
	comparisonPrice,
	discountType,
	id,
	images,
	onCampaignPackageAddToCartClick,
	onCampaignPackageAddToWishlistClick,
	priceLabel,
	stafflingPrice,
	title,
	validToDate,
}: Props) {
	const { tPlural } = useI18n();
	const formatDate = useDateFormatter();

	const [isOpen, setIsOpen] = useState(false);
	const { isLoading, isSellable, items, priceExcVat, priceIncVat } =
		useCampaignProducts(id, isOpen);
	const { usePriceExcVat } = usePriceContext();
	// All campaigns that should have a package price, has one from the api
	// so use that for the popover.
	const packagePrice = usePriceExcVat ? priceExcVat : priceIncVat;

	const { gtmItemListId, gtmItemListName, sendViewItemListEvent } =
		useProductListGTMEvents(
			'pdp_mix_match',
			`Mix and Match: [${discountType}] - [${title}] ([${id}])`,
		);

	useEffect(() => {
		if (items?.length) {
			sendViewItemListEvent(items, items.length);
		}
	}, [items, sendViewItemListEvent]);

	const validToText = formatTemplate(campaignValidToText, {
		validTo: formatDate(validToDate),
	});
	const validToDaysToShow = Number(
		publicEnvConfig.CAMPAIGN_VALID_TO_DAYS_TO_SHOW ?? '15',
	);
	const showValidTo = validToDate
		? isDateAfter(
				new Date(),
				subtractDaysFromDate(new Date(validToDate), validToDaysToShow),
			)
		: false;

	const isBuyAll = is.oneOf(
		discountType,
		'BuyAllFixedPrice',
		'BuyAllCheapestFree',
		'BuyAllToLoweredUnitPrice',
	);

	const image1 = images?.[0];
	const image2 = images?.[1];
	const image =
		image1 && image2 ? (
			<span className="relative inline-block size-12">
				<Img
					src={image1.src}
					alt=""
					width={32}
					height={32}
					className="absolute top-0 left-0"
					jpgOptimized={false}
				/>
				<Img
					src={image2.src}
					alt=""
					width={32}
					height={32}
					className="absolute right-0 bottom-0 z-1"
					jpgOptimized={false}
				/>
			</span>
		) : image1 ? (
			<Img
				src={image1.src}
				alt=""
				width={48}
				height={48}
				jpgOptimized={false}
			/>
		) : null;

	return (
		<>
			<LinkCard
				title={title}
				subtitle={showValidTo ? validToText : undefined}
				image={image}
				price={stafflingPrice}
				priceLabel={priceLabel}
				comparisonPrice={comparisonPrice}
				onClick={() => setIsOpen(true)}
			/>
			<Popover
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				id={`product-details-campaign-products-${id}`}
				title={title}
			>
				<>
					<ScreenReaderAnnouncementText
						as="p"
						text={
							items ? tPlural('general_amount_of_products', items.length) : ''
						}
						hidden
						atomic
					/>
					{comparisonPrice?.title && (
						<PriceText
							as="p"
							price={comparisonPrice}
							prefix={comparisonPrice.title}
							className="text-sm"
						/>
					)}
					{showValidTo && validToText && (
						<Text as="pSmall" text={validToText} />
					)}

					{isBuyAll && (
						<CampaignBuyAllProducts
							discountType={discountType}
							id={id}
							isLoading={isLoading}
							isSellable={isSellable}
							onAddToCartClick={onCampaignPackageAddToCartClick}
							onAddToWishlistClick={onCampaignPackageAddToWishlistClick}
							packagePrice={packagePrice}
							priceLabel={priceLabel}
							rows={items}
							title={title}
						/>
					)}
					{!isBuyAll && (
						<CampaignBuyAnyProducts
							gtmItemListId={gtmItemListId}
							gtmItemListName={gtmItemListName}
							isLoading={isLoading}
							rows={items}
						/>
					)}
				</>
			</Popover>
		</>
	);
}
Campaign.displayName = 'Campaign';
